import React, { useEffect, useMemo, useState } from "react";
import { api } from "@/lib/api";
import { toast } from "sonner";
import { X, Save, Plus, Trash2, FileText } from "lucide-react";
import { fmtTRY } from "@/lib/format";

/**
 * QuoteEditModal — Fiyat Teklifleri sayfasından bir teklifin
 * temel alanlarını (başlık, cari, tarih, notlar, KDV, vade, ödeme %)
 * ve kalemlerini (adet, karton toplam, tedarikçi maliyet, teslim tarihi)
 * düzenlemeyi sağlar. Kaydet → PATCH /price-quotes/{qid}
 */
export default function QuoteEditModal({ quote, contacts, onClose, onSaved }) {
  const [saving, setSaving] = useState(false);
  const [form, setForm] = useState(() => ({
    title: quote.title || "",
    contact_id: quote.contact_id || "",
    date: quote.date || "",
    notes: quote.notes || "",
    vat_rate: quote.vat_rate ?? 20,
    payment_term_days: quote.payment_term_days ?? 0,
    mold_cost: quote.mold_cost ?? 0,
    cliche_cost: quote.cliche_cost ?? 0,
    extra_shipping_cost: quote.extra_shipping_cost ?? 0,
    pallet_cost_per_unit: quote.pallet_cost_per_unit ?? 0,
    pallet_deposit: quote.pallet_deposit ?? true,
    items: (quote.items && quote.items.length > 0) ? quote.items.map((it) => ({ ...it })) : null,
    quantity: quote.quantity ?? 1,
    karton_total: quote.karton_total ?? 0,
  }));

  const isMultiItem = Array.isArray(form.items) && form.items.length > 0;

  const customers = useMemo(
    () => contacts.filter((c) => c.type === "customer" || c.type === "both"),
    [contacts]
  );

  const setItem = (idx, patch) => {
    setForm((f) => {
      const items = [...(f.items || [])];
      items[idx] = { ...items[idx], ...patch };
      return { ...f, items };
    });
  };
  const removeItem = (idx) => {
    if (!window.confirm("Kalem silinsin mi?")) return;
    setForm((f) => ({ ...f, items: (f.items || []).filter((_, i) => i !== idx) }));
  };

  const itemsTotal = useMemo(() => {
    if (!isMultiItem) return Number(form.karton_total || 0);
    return (form.items || []).reduce((s, it) => s + Number(it.karton_total || 0), 0);
  }, [form.items, form.karton_total, isMultiItem]);

  const grandTotal = itemsTotal + Number(quote.shipping_total || 0);

  const save = async () => {
    if (!form.contact_id) { toast.error("Cari seçin"); return; }
    setSaving(true);
    try {
      const payload = {
        title: form.title,
        contact_id: form.contact_id,
        date: form.date,
        notes: form.notes,
        vat_rate: Number(form.vat_rate) || 0,
        payment_term_days: Number(form.payment_term_days) || 0,
        mold_cost: Number(form.mold_cost) || 0,
        cliche_cost: Number(form.cliche_cost) || 0,
        extra_shipping_cost: Number(form.extra_shipping_cost) || 0,
        pallet_cost_per_unit: Number(form.pallet_cost_per_unit) || 0,
        pallet_deposit: !!form.pallet_deposit,
      };
      if (isMultiItem) {
        payload.items = form.items;
      } else {
        payload.quantity = Number(form.quantity) || 1;
        payload.karton_total = Number(form.karton_total) || 0;
      }
      await api.patch(`/price-quotes/${quote.id}`, payload);
      toast.success("Teklif güncellendi");
      onSaved && onSaved();
      onClose();
    } catch (e) {
      toast.error("Güncellenemedi: " + (e?.response?.data?.detail || e?.message || ""));
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="fixed inset-0 z-50 bg-black/40 flex items-center justify-center p-4"
         onClick={onClose} data-testid="quote-edit-backdrop">
      <div onClick={(e) => e.stopPropagation()} data-testid="quote-edit-modal"
           className="bg-white border-2 border-black w-full max-w-4xl max-h-[92vh] overflow-hidden flex flex-col">
        <div className="flex items-center justify-between p-5 border-b-2 border-black">
          <h2 className="font-display text-xl font-bold flex items-center gap-2">
            <FileText size={18} /> Teklifi Düzenle
          </h2>
          <button onClick={onClose} className="p-1"><X size={18} /></button>
        </div>

        <div className="flex-1 overflow-y-auto p-5 space-y-5">
          {/* Meta alanları */}
          <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
            <F label="Başlık (opsiyonel)">
              <input className="input" data-testid="quote-edit-title" value={form.title}
                     onChange={(e) => setForm({ ...form, title: e.target.value })} />
            </F>
            <F label="Cari">
              <select className="input" data-testid="quote-edit-contact" value={form.contact_id}
                      onChange={(e) => setForm({ ...form, contact_id: e.target.value })}>
                <option value="">— Seçin —</option>
                {customers.map((c) => <option key={c.id} value={c.id}>{c.name}</option>)}
              </select>
            </F>
            <F label="Tarih">
              <input type="date" className="input" data-testid="quote-edit-date" value={form.date}
                     onChange={(e) => setForm({ ...form, date: e.target.value })} />
            </F>
            <F label="KDV %">
              <input type="number" step="0.1" className="input font-mono-num text-right"
                     data-testid="quote-edit-vat"
                     value={form.vat_rate}
                     onChange={(e) => setForm({ ...form, vat_rate: e.target.value })} />
            </F>
            <F label="Vade (gün)">
              <input type="number" step="1" className="input font-mono-num text-right"
                     data-testid="quote-edit-term"
                     value={form.payment_term_days}
                     onChange={(e) => setForm({ ...form, payment_term_days: e.target.value })} />
            </F>
            <F label="Palet Bedeli (kutu başı)">
              <input type="number" step="0.01" className="input font-mono-num text-right"
                     data-testid="quote-edit-pallet"
                     value={form.pallet_cost_per_unit}
                     onChange={(e) => setForm({ ...form, pallet_cost_per_unit: e.target.value })} />
            </F>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
            <F label="Kalıp Bedeli">
              <input type="number" step="0.01" className="input font-mono-num text-right"
                     value={form.mold_cost}
                     onChange={(e) => setForm({ ...form, mold_cost: e.target.value })} />
            </F>
            <F label="Klişe Bedeli">
              <input type="number" step="0.01" className="input font-mono-num text-right"
                     value={form.cliche_cost}
                     onChange={(e) => setForm({ ...form, cliche_cost: e.target.value })} />
            </F>
            <F label="Ekstra Nakliye">
              <input type="number" step="0.01" className="input font-mono-num text-right"
                     value={form.extra_shipping_cost}
                     onChange={(e) => setForm({ ...form, extra_shipping_cost: e.target.value })} />
            </F>
          </div>

          <F label="Notlar">
            <textarea rows={2} className="input" data-testid="quote-edit-notes" value={form.notes}
                      onChange={(e) => setForm({ ...form, notes: e.target.value })} />
          </F>

          {/* Kalemler */}
          {isMultiItem ? (
            <div className="border-2 border-black/10 p-3">
              <div className="flex items-center justify-between mb-2">
                <div className="label-caps">Kalemler ({form.items.length})</div>
                <div className="text-[11px] text-neutral-500 font-mono-num">Toplam: {fmtTRY(itemsTotal)}</div>
              </div>
              <div className="overflow-x-auto">
                <table className="w-full text-xs">
                  <thead>
                    <tr className="border-b border-black/10">
                      <th className="text-left px-2 py-1.5 label-caps">Kalem</th>
                      <th className="text-right px-2 py-1.5 label-caps w-20">Adet</th>
                      <th className="text-right px-2 py-1.5 label-caps w-28">Karton Top.</th>
                      <th className="text-right px-2 py-1.5 label-caps w-24">Ted. ₺/m²</th>
                      <th className="text-right px-2 py-1.5 label-caps w-28">Ted. Toplam</th>
                      <th className="w-8"></th>
                    </tr>
                  </thead>
                  <tbody>
                    {form.items.map((it, i) => (
                      <tr key={i} className="border-b border-black/5 align-top">
                        <td className="px-2 py-1.5">
                          <input className="input py-1 text-xs mb-1" placeholder="Kalem başlığı (opsiyonel)"
                                 data-testid={`quote-edit-item-title-${i}`}
                                 value={it.title || ""}
                                 onChange={(e) => setItem(i, { title: e.target.value })} />
                          <div className="text-[10px] text-neutral-500">
                            {it.flute && <b>{it.flute}</b>}{it.quality && ` · ${it.quality}`}
                            {it.length_mm != null && ` · ${it.length_mm}×${it.width_mm}×${it.height_mm}`}
                            {it.box_type === "plaka" && it.sheet_width_mm != null && ` · ${it.sheet_width_mm}×${it.sheet_length_mm}`}
                          </div>
                        </td>
                        <td className="px-2 py-1.5">
                          <input type="number" step="1" min="1"
                                 className="input py-1 font-mono-num text-right text-xs"
                                 data-testid={`quote-edit-item-qty-${i}`}
                                 value={it.quantity ?? 1}
                                 onChange={(e) => setItem(i, { quantity: e.target.value })} />
                        </td>
                        <td className="px-2 py-1.5">
                          <input type="number" step="0.01"
                                 className="input py-1 font-mono-num text-right text-xs"
                                 data-testid={`quote-edit-item-karton-${i}`}
                                 value={it.karton_total ?? 0}
                                 onChange={(e) => setItem(i, { karton_total: e.target.value })} />
                        </td>
                        <td className="px-2 py-1.5">
                          <input type="number" step="0.01"
                                 className="input py-1 font-mono-num text-right text-xs"
                                 value={it.cost_unit ?? 0}
                                 onChange={(e) => setItem(i, { cost_unit: e.target.value })} />
                        </td>
                        <td className="px-2 py-1.5">
                          <input type="number" step="0.01"
                                 className="input py-1 font-mono-num text-right text-xs"
                                 value={it.cost_total ?? 0}
                                 onChange={(e) => setItem(i, { cost_total: e.target.value })} />
                        </td>
                        <td className="px-2 py-1.5 text-center">
                          <button onClick={() => removeItem(i)}
                                  data-testid={`quote-edit-item-remove-${i}`}
                                  className="p-1 hover:bg-rose-50 text-rose-600"
                                  title="Kalemi sil"
                                  disabled={form.items.length <= 1}>
                            <Trash2 size={12} />
                          </button>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
              <div className="text-[10px] text-neutral-500 mt-2">
                Not: Adet/karton toplamı değişince genel toplam yeniden hesaplanır. Kalem eklemek için Fiyat Hesaplama'ya dönüp yeni teklif oluşturmayı öneririz.
              </div>
            </div>
          ) : (
            <div className="grid grid-cols-1 md:grid-cols-2 gap-3 border-2 border-black/10 p-3">
              <F label="Adet">
                <input type="number" step="1" min="1" className="input font-mono-num text-right"
                       data-testid="quote-edit-quantity"
                       value={form.quantity}
                       onChange={(e) => setForm({ ...form, quantity: e.target.value })} />
              </F>
              <F label="Karton Toplam">
                <input type="number" step="0.01" className="input font-mono-num text-right"
                       data-testid="quote-edit-karton"
                       value={form.karton_total}
                       onChange={(e) => setForm({ ...form, karton_total: e.target.value })} />
              </F>
            </div>
          )}

          <div className="flex items-center justify-between border-t border-black/10 pt-3">
            <div className="text-xs text-neutral-500 font-mono-num">
              Karton: {fmtTRY(itemsTotal)} + Nakliye: {fmtTRY(quote.shipping_total)} = <b className="text-emerald-700">{fmtTRY(grandTotal)}</b>
            </div>
          </div>
        </div>

        <div className="p-4 border-t border-black flex items-center justify-end gap-2 bg-neutral-50">
          <button onClick={onClose}
                  className="px-4 py-2 text-xs uppercase tracking-widest border border-black/30 hover:bg-white">
            Vazgeç
          </button>
          <button onClick={save} disabled={saving}
                  data-testid="quote-edit-save"
                  className="brutal-btn bg-black text-white px-4 py-2 text-sm flex items-center gap-2 disabled:opacity-40">
            <Save size={14} /> {saving ? "Kaydediliyor…" : "Kaydet"}
          </button>
        </div>
      </div>
    </div>
  );
}

function F({ label, children }) {
  return (
    <label className="block">
      <div className="label-caps mb-1">{label}</div>
      {children}
    </label>
  );
}
